/**
 * MemberAvatars — compact avatar stack for shared projects/lists.
 *
 * Shows the first 2 characters of each member's email as initials inside
 * a small colored circle. Colors are deterministic from email via `stringToColor`.
 * Excludes the current user. Overlaps when there are many members.
 *
 * **Sidebar context** (default): 20px circles, tight overlap.
 * **Main context** (future): larger circles, more detail — see docs/avatar-system-vision.md.
 *
 * Ported from v1's `GetAvatars.tsx`, rebuilt without MUI.
 */

import { memo } from "react"
import {
  stringToColor,
  invertColor,
  alphaBlend,
} from "@/shared/utils/color/colorUtils"
import { cn } from "@/shared/utils/general/cn"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MemberInfo {
  email: string
  pending?: boolean
}

interface MemberAvatarsProps {
  /** Array of member emails to display (already filtered to exclude current user) */
  members: MemberInfo[]
  /** Whether the parent item is selected/active */
  selected?: boolean
  /** Max number of avatars to show before "+N" overflow */
  max?: number
  className?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MemberAvatars = memo(function MemberAvatars({
  members,
  selected: _selected = false,
  max = 3,
  className,
}: MemberAvatarsProps) {
  void _selected // used for future border-color matching
  if (!members || members.length === 0) return null

  const visible = members.slice(0, max)
  const overflow = members.length - max

  return (
    <span className={cn("inline-flex shrink-0 items-center", className)}>
      {visible.map((member, i) => {
        const bgHex = stringToColor(member.email)
        const bg = alphaBlend(bgHex, 0.8)
        const textColor = invertColor(bgHex)
        // Overlap: first avatar at 0, subsequent overlap by -6px
        const ml = i === 0 ? 0 : -9

        return (
          <span
            key={member.email + i}
            title={member.email}
            className="inline-flex items-center justify-center rounded-full"
            style={{
              width: 23,
              height: 23,
              minWidth: 23,
              fontSize: "0.65rem",
              fontWeight: 600,
              lineHeight: 1,
              backgroundColor: bg,
              color: textColor,
              opacity: member.pending ? 0.25 : 1,
              marginLeft: ml,
              border: `1.5px solid var(--sidebar-surface, var(--sidebar))`,
              // Stack right avatars on top of left ones (v1 parity — later = on top)
              zIndex: i,
              position: "relative",
            }}
          >
            {member.email.slice(0, 2).toLowerCase()}
          </span>
        )
      })}
      {overflow > 0 && (
        <span
          className="inline-flex items-center justify-center rounded-full"
          style={{
            width: 23,
            height: 23,
            minWidth: 23,
            fontSize: "0.65rem",
            fontWeight: 600,
            lineHeight: 1,
            backgroundColor: "var(--muted)",
            color: "var(--muted-foreground)",
            marginLeft: -6,
            border: `1.5px solid var(--sidebar-surface, var(--sidebar))`,
            position: "relative",
            zIndex: 0,
          }}
        >
          +{overflow}
        </span>
      )}
    </span>
  )
})
